import { PerformanceDatapoint } from 'owa-analytics';
import {
    lazyGetHasSharingIssues,
    lazyGetPendingAttachmentString,
    lazyGetSharingIssuesString,
} from 'owa-attachment-block-on-send';
import type { AttachmentWellViewState } from 'owa-attachment-well-data';
import type { FullComposeViewState } from 'owa-calendar-compose-types';
import getCurrentEventOrganizer from 'owa-calendar-compose-form-store/lib/selectors/getCurrentEventOrganizer';
import { getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import type { RecipientContainer } from 'owa-link-data';
import loc, { isStringNullOrWhiteSpace } from 'owa-localize';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import getFromAddress from '../utils/getFromAddress';
import {
    attachmentInProgressCalendarTitle,
    sharingIssueCalendarTitle,
} from './verifyAttachmentStatusBeforeSave.locstring.json';
import { sendAnyway } from '../orchestrators/verifyEventThenSave.locstring.json';

let getPendingAttachmentString;
let getHasSharingIssues;
let getSharingIssuesString;
export async function verifyAttachmentStatusBeforeSave(
    event: CalendarEvent,
    hasWaitingAttachments: boolean,
    isCollabSpaceValid: boolean,
    formId: string,
    targetWindow: Window
) {
    const viewState: FullComposeViewState = getFullComposeViewState(formId);
    if (
        !viewState ||
        !viewState.attachmentWell ||
        !containsAttachmentsOrLinks(viewState.attachmentWell)
    ) {
        return true;
    }

    const datapoint = new PerformanceDatapoint('AttachmentCalendarBlockOnSend');

    if (!getPendingAttachmentString || !getHasSharingIssues || !getSharingIssuesString) {
        getPendingAttachmentString = await lazyGetPendingAttachmentString.import();
        getHasSharingIssues = await lazyGetHasSharingIssues.import();
        getSharingIssuesString = await lazyGetSharingIssuesString.import();
    }

    // We do not allow sending if an attachment operation (change permission, create, delete, convert) is in progress
    // This is because it is possible for the event to be sent with the attachment in a transient state, leading to a broken experience
    // We also alert the user if attendees will not be able to access links being sent, but will let the user send anyway if they want
    const pendingAttachmentString: string = getPendingAttachmentString(
        viewState.attachmentWell,
        hasWaitingAttachments /* has waiting attachments */
    );

    // We first check for any in progress attachment operations
    if (!isStringNullOrWhiteSpace(pendingAttachmentString)) {
        await confirm(loc(attachmentInProgressCalendarTitle), pendingAttachmentString, false, {
            okText: loc(okButton),
            hideCancelButton: true,
            targetWindow: targetWindow,
        });

        // return regardless of response, the event is not in a state where it can be saved/sent
        datapoint.addCustomData({
            endReason: 'attachmentActionInProgress',
        });
        datapoint.end();
        return false;
    }

    const organizer = getCurrentEventOrganizer(formId);
    const fromEmailAddress: string = getFromAddress(formId, organizer);
    const recipientContainers: RecipientContainer[] = [
        event.RequiredAttendees,
        event.OptionalAttendees,
    ];

    // We now check for links where at least one attendee will not be able to access the content of the link.
    // infobarIds is always empty in calendar since it is used only for reference attachment sharing tips,
    // which are not supported.
    if (
        viewState &&
        (await getHasSharingIssues(
            [] /* infobarids */,
            viewState.attachmentWell,
            fromEmailAddress,
            recipientContainers,
            true /* isCalendar */
        ))
    ) {
        const response: DialogResponse = await confirm(
            loc(sharingIssueCalendarTitle),
            getSharingIssuesString(
                [] /* viewState.infoBarIds */,
                viewState.attachmentWell,
                fromEmailAddress,
                recipientContainers,
                null /* composeId */,
                true /* isCalendar */,
                isCollabSpaceValid
            ),
            false,
            {
                okText: loc(sendAnyway),
                targetWindow: targetWindow,
            }
        );
        if (response !== DialogResponse.ok) {
            // If we don't choose save, then do nothing
            datapoint.addCustomData({
                endReason: 'sharingIssueUserCanceledSave',
            });
            datapoint.end();
            return false;
        } else {
            datapoint.addCustomData({
                endReason: 'sharingIssueUserSavedAnyway',
            });
            datapoint.end();
            return true;
        }
    }

    datapoint.addCustomData({
        endReason: 'saveNotBlocked',
    });
    datapoint.end();
    return true;
}

function containsAttachmentsOrLinks(attachmentWell: AttachmentWellViewState) {
    return (
        attachmentWell.docViewAttachments.length > 0 ||
        attachmentWell.imageViewAttachments.length > 0 ||
        attachmentWell.inlineAttachments.length > 0 ||
        attachmentWell.sharingLinkIds.length > 0
    );
}
